

/**
 * Landing Page Component - Main Production Landing Page
 *
 * This is the primary marketing landing page for ALICE, an AI-native recruiter platform.
 * The component presents a comprehensive overview of the product, its value propositions,
 * features, and business impact to convert visitors into leads.
 *
 * @module Landing
 * @description
 * A full-featured React component that serves as the main entry point for the Candid-i
 * recruitment platform. It showcases ALICE's capabilities through multiple content sections
 * with interactive elements, animations, and lead capture functionality.
 *
 * @features
 * - Sticky navigation header with smooth scroll anchors
 * - Hero section with animated character image and gradient background
 * - Solution overview with 3-card grid explaining ALICE's approach
 * - Value proposition section with alternating image/text layouts (3 hero sections)
 * - Feature showcase highlighting key differentiators (24/7 operation, speed, sourcing, results)
 * - Business impact section with statistics and engagement models
 * - Target audience section ("Who ALICE Is For")
 * - Horizontal scrolling testimonials carousel
 * - Comprehensive footer with social links and navigation
 * - Request Access modal with form submission to backend API
 *
 * @state
 * - showRequestAccessModal: Controls visibility of the access request form modal
 * - requestAccessData: Form data object containing firstName, lastName, businessEmail,
 *   companySize, and role fields
 *
 * @interactions
 * - Book Demo: Opens external Calendly link in new tab
 * - Request Access: Opens modal form for lead capture
 * - Form Submission: POSTs to /api/request-access endpoint
 * - Smooth Scroll: Navigation links scroll to anchor sections
 *
 * @styling
 * - Uses Tailwind CSS for utility-first styling
 * - Custom animations from landing-animations.css
 * - Brand colors: Orange (#f7911d), warm gradients (#FFF0DE, #fef7f0)
 * - Responsive design with mobile-first approach
 * - Roboto font family loaded via Google Fonts
 *
 * @dependencies
 * - react-router-dom: For navigation (useNavigate)
 * - Custom animations CSS: landing-animations.css
 * - API config: API_BASE_URL from config
 * - Assets: LogoAnimate.svg, hero-character.jpeg, various section images
 *
 * @api
 * - POST /api/request-access: Submits access request form data
 *
 * @design
 * Aligned with mockup design from public/Woondamockupindex.html
 */

import {
  CalendarCheck,
  FileChartColumn,
  Map,
  MessagesSquare,
  ScanSearch,
  Search,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import candidILogo from '../assets/images/candid-i-logo.png';
import { submitPublicEnquiry } from '../services/publicEnquiryApi';
import '../styles/landing-animations.css';


const defaultLeadForm = {
  firstName: '',
  lastName: '',
  businessEmail: '',
  organisation: '',
  interest: '',
  brief: '',
  profileReference: '',
};

const heroKpiRanges = {
  mandates: [28, 75],
  profiles: [1180, 2420],
  briefings: [14, 82],
  markets: [7, 45],
};

const heroKpiSeed = {
  mandates: 42,
  profiles: 1420,
  briefings: 22,
  markets: 11,
};

const audiencePaths = [
  {
    id: 'employers',
    eyebrow: 'PATH 01 · EMPLOYERS',
    title: 'Move faster on the right talent.',
    description:
      'See the market clearly, identify relevant people earlier, and turn intelligence into a stronger shortlist.',
    bullets: [
      'Map market movement and competitor activity',
      'Surface hidden talent before it is visible elsewhere',
      'Reduce time between signal and action',
    ],
    cta: 'Hire with us',
  },
  {
    id: 'candidates',
    eyebrow: 'PATH 02 · EXECUTIVE TALENT',
    title: 'Stay visible without losing discretion.',
    description:
      'Receive relevant opportunities, understand market demand, and move when the timing is right.',
    bullets: [
      'Relevant opportunities, not noise',
      'Discreet visibility across the network',
      'Better conversations at the right time',
    ],
    cta: 'Join the talent network',
  },
];

const problemCards = [
  {
    title: 'What is changing?',
    description:
      'Market conditions shift quickly — candidate demand, competitor activity, and role movement can change before teams notice.',
  },
  {
    title: 'Why is it hard to see?',
    description:
      'The best talent is often hidden across firms, sectors, and networks that traditional search methods miss.',
  },
  {
    title: 'Why do profiles fail?',
    description:
      'CVs show experience, but not readiness, intent, or strategic fit.',
  },
  {
    title: 'Why do decisions slow down?',
    description:
      'Decision-makers need context and interpretation, not a longer list of names.',
  },
];

const solutionSteps = [
  {
    step: '01',
    title: 'Monitor',
    description:
      'Tracks movement across sectors, firms, and profiles in real time.',
  },
  {
    step: '02',
    title: 'Interpret',
    description:
      'Turns raw signal into context, curation, and market intelligence.',
  },
  {
    step: '03',
    title: 'Act',
    description:
      'Moves the right next step into motion: outreach, scheduling, reporting, and publishing.',
  },
];

const aliceCapabilities = [
  {
    id: 'market-mapping',
    title: 'Maps markets',
    description: 'Builds live views of talent supply, movement, and competitive density.',
    icon: Map,
  },
  {
    id: 'talent-identification',
    title: 'Finds talent',
    description: 'Identifies relevant profiles across markets before they appear on the open market.',
    icon: Search,
  },
  {
    id: 'candidate-conversations',
    title: 'Starts conversations',
    description: 'Opens personalized, discreet candidate outreach at optimal timing.',
    icon: MessagesSquare,
  },
  {
    id: 'interest-and-fit',
    title: 'Screens interest',
    description: 'Assesses willingness to move, commercial relevance, and fit.',
    icon: ScanSearch,
  },
  {
    id: 'interview-coordination',
    title: 'Schedules interviews',
    description: 'Coordinates candidate briefings between hiring leaders and high-fit profiles.',
    icon: CalendarCheck,
  },
  {
    id: 'market-intelligence',
    title: 'Produces intelligence reports',
    description: 'Turns market movement into structured market briefs and commentary.',
    icon: FileChartColumn,
  },
];

const talentFilters = [
  'All',
  'Commodities',
  'Financial Markets',
  'Technology',
  'Private Capital',
  'Wealth Management',
  'Banking',
];

const talentProfiles = [
  {
    id: 'CAND-104',
    market: 'Singapore',
    sector: 'Technology',
    role: 'Senior AI Engineer',
    specialism: 'Enterprise AI Delivery',
    marketSignal: 'Scaling AI delivery programmes',
    marketStatus: 'Selectively open',
    marketRelevance: 'Scarce enterprise AI experience',
    image:
      'https://images.unsplash.com/photo-1560250097-0b93528c311a?auto=format&fit=crop&w=1200&q=80',
    imageAlt: 'Illustrative executive portrait for an anonymised senior AI engineer profile',
    imagePosition: '50% 18%',
  },
  {
    id: 'CAND-087',
    market: 'London',
    sector: 'Financial Markets',
    role: 'FinTech Partnerships Director',
    specialism: 'B2B Platform Growth',
    marketSignal: 'Built partnerships across EMEA',
    marketStatus: 'Open to discussion',
    marketRelevance: 'Cross-border growth experience',
    image:
      'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=1200&q=80',
    imageAlt: 'Illustrative portrait for an anonymised FinTech partnerships profile',
  },
  {
    id: 'CAND-122',
    market: 'Singapore',
    sector: 'Commodities',
    role: 'Precious Metals Sales Lead',
    specialism: 'Institutional Client Coverage',
    marketSignal: 'Expanding APAC institutional relationships',
    marketStatus: 'Confidentially engaged',
    marketRelevance: 'Established regional client network',
    image:
      'https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?auto=format&fit=crop&w=1200&q=80',
    imageAlt: 'Illustrative portrait for an anonymised precious metals sales profile',
  },
  {
    id: 'CAND-128',
    market: 'New York',
    sector: 'Private Capital',
    role: 'Head of Quantitative Research',
    specialism: 'Systematic Investment Research',
    marketSignal: 'Building a quantitative research function',
    marketStatus: 'Not actively looking',
    marketRelevance: 'Specialist research leadership',
    image:
      'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=1200&q=80',
    imageAlt: 'Illustrative portrait for an anonymised quantitative research profile',
  },
  {
    id: 'CAND-139',
    market: 'Dubai',
    sector: 'Wealth Management',
    role: 'Regional Wealth Director',
    specialism: 'Private Client Growth',
    marketSignal: 'Expanding an international client portfolio',
    marketStatus: 'Selectively open',
    marketRelevance: 'Cross-border wealth leadership',
    image:
      'https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=1200&q=80',
    imageAlt: 'Illustrative executive portrait for an anonymised wealth management profile',
    imagePosition: '50% 38%',
  },
  {
    id: 'CAND-145',
    market: 'Hong Kong',
    sector: 'Banking',
    role: 'Payments Product Lead',
    specialism: 'Transaction Banking Platforms',
    marketSignal: 'Leading API-enabled payments growth',
    marketStatus: 'Open to discussion',
    marketRelevance: 'Regional payments transformation experience',
    image:
      'https://images.unsplash.com/photo-1519085360753-af0119f7cbe7?auto=format&fit=crop&w=1200&q=80',
    imageAlt: 'Illustrative portrait for an anonymised payments leadership profile',
  },
];

const publishingData = {
  featured: {
    type: 'Video brief',
    time: '2 min watch',
    title: "The human side of advising India's global wealth families",
    description:
      'A focused conversation on trust, judgement and the human relationships behind global wealth advisory.',
    date: 'Jul 30, 2026',
    image:
      'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=80',
    videoUrl:
      'https://videos.hubbis.com/zeherra-mecklai-on-the-human-side-of-advising-india-s-global-wealth-families.mp4',
  },
  items: [
    {
      type: 'Curated article',
      source: 'Financial Times',
      tag: 'Financial Times',
      title: 'What changed in European payments executive hiring this month',
      description:
        'Candid-I note: payment infrastructure remains active, but commercial leadership roles are becoming more selective.',
      date: 'Oct 21, 2025',
    },
    {
      type: 'Quarterly brief',
      source: 'FinTech',
      tag: 'FinTech',
      title: 'UK FinTech hiring softens in Q3 while APAC commercial roles expand',
      description:
        'Candidate supply remains selective across mid-senior roles. Demand is moving toward distribution and partnerships.',
      date: 'Oct 20, 2025',
    },
    {
      type: 'Talent map',
      source: 'Commodities',
      tag: 'Commodities',
      title: 'Global LNG Trading Supply & Density Map (Q4 Update)',
      description:
        'Mapping 160+ talent nodes across Geneva, Houston, and Singapore hubs for leadership and front office roles.',
      date: 'Oct 19, 2025',
    },
  ],
};

const clientOutputs = [
  { label: 'Market map', icon: Map },
  { label: 'Target-company analysis', icon: ScanSearch },
  { label: 'Relevant shortlist', icon: Search },
  { label: 'Candidate engagement', icon: MessagesSquare },
  { label: 'Interview coordination', icon: CalendarCheck },
  { label: 'Intelligence report', icon: FileChartColumn },
];

function trackLandingEvent(event, details = {}) {
  if (typeof window === 'undefined') return;
  const payload = { event, ...details };
  window.dataLayer?.push(payload);
  window.dispatchEvent(new CustomEvent('candidi:analytics', { detail: payload }));
}

const employerValue = [
  'Visibility into talent supply and competitor demand',
  'Earlier access to relevant, hard-to-reach professionals',
  'Evidence-led shortlisting and market context',
  'Human judgement supported by structured intelligence',
];

const candidateValue = [
  'Relevant opportunities, not indiscriminate outreach',
  'Discreet access to roles beyond the open market',
  'Context on the company, mandate and timing',
  'Human conversations at the right stage',
];

const outcomes = [
  {
    value: 'Faster orientation',
    label: 'Understand the addressable market before outreach begins.',
  },
  {
    value: 'Earlier access',
    label: 'Reach relevant professionals beyond active applicants.',
  },
  {
    value: 'Better context',
    label: 'Evaluate talent with competitor and market intelligence.',
  },
  {
    value: 'Discreet engagement',
    label: 'Protect confidentiality throughout the search.',
  },
];

const processSteps = [
  {
    step: '01',
    title: 'Define',
    description: 'Clarify the role, market or workforce question that needs to be answered.',
  },
  {
    step: '02',
    title: 'Map',
    description:
      'Build the talent landscape and identify the signals, organisations and people that matter.',
  },
  {
    step: '03',
    title: 'Act',
    description:
      'Turn the intelligence into outreach, hiring recommendations or a market strategy.',
  },
];

const modalCopy = {
  employer: {
    title: 'Hire with us',
    subtitle:
      'Tell us what you need. We define the market, map relevant talent and return with an evidence-led search strategy.',
    interest: 'Hiring support',
    button: 'Send to Candid-I',
  },
  candidate: {
    title: 'Join the talent network',
    subtitle: 'Share your profile and we will match you to relevant opportunities.',
    interest: 'Join the talent network',
    button: 'Send to Candid-I',
  },
  intelligence: {
    title: 'Join the market briefing',
    subtitle: 'Tell us which sectors you want to track and how we can help.',
    interest: 'Market intelligence',
    button: 'Send to Candid-I',
  },
  profile: {
    title: 'Request a confidential profile brief',
    subtitle:
      'Tell us which capability is relevant to your hiring plans. We’ll contact you to understand the mandate before sharing further information.',
    interest: 'Confidential profile brief',
    button: 'Request brief',
  },
  general: {
    title: 'Start a conversation',
    subtitle: 'Tell us how Candid-I can help with hiring, talent, or market intelligence.',
    interest: 'General enquiry',
    button: 'Send to Candid-I',
  },
};

function SectionLabel({ children, dark = false, className = '' }) {
  return (
    <p
      className={`mb-3 text-[11px] font-semibold uppercase tracking-[0.28em] ${
        dark ? 'text-[#f7911d]/90' : 'text-[#f7911d]'
      } font-mono ${className}`}
    >
      {children}
    </p>
  );
}

function SectionHeading({ eyebrow, title, subtitle, dark = false, align = 'left', titleId }) {
  return (
    <div className={`${align === 'center' ? 'mx-auto text-center' : ''} max-w-3xl`}>
      {eyebrow ? <SectionLabel dark={dark}>{eyebrow}</SectionLabel> : null}
      <h2
        id={titleId}
        className={`text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight ${
          dark ? 'text-white' : 'text-[#07111f]'
        }`}
      >
        {title}
      </h2>
      {subtitle ? (
        <p
          className={`mt-4 text-base md:text-lg leading-relaxed ${
            dark ? 'text-white/70' : 'text-[#5b6475]'
          }`}
        >
          {subtitle}
        </p>
      ) : null}
    </div>
  );
}

function getRandomInRange(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function AnimatedNumber({ value, format = (n) => n.toLocaleString(), className = '' }) {
  const [displayValue, setDisplayValue] = useState(value);
  const displayValueRef = useRef(value);

  useEffect(() => {
    displayValueRef.current = displayValue;
  }, [displayValue]);

  useEffect(() => {
    let frameId;
    let startTime;
    const startValue = displayValueRef.current;
    const diff = value - startValue;
    const duration = 450;

    const tick = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      const nextValue = startValue + diff * eased;
      setDisplayValue(progress === 1 ? value : nextValue);

      if (progress < 1) {
        frameId = window.requestAnimationFrame(tick);
      }
    };

    frameId = window.requestAnimationFrame(tick);

    return () => window.cancelAnimationFrame(frameId);
  }, [value]);

  return <span className={className}>{format(Math.round(displayValue))}</span>;
}

function HeroKpiStrip() {
  const [stats, setStats] = useState(heroKpiSeed);
  const stripRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const advanceWithinBand = (currentValue, [min, max], stepMin, stepMax) => {
      const step = getRandomInRange(stepMin, stepMax);
      const nextValue = currentValue + step;

      if (currentValue >= max) {
        return min + getRandomInRange(0, Math.max(0, Math.floor((max - min) * 0.18)));
      }

      if (nextValue > max) {
        return min + (nextValue - max - 1);
      }

      return nextValue;
    };

    const buildNextStats = (currentStats) => ({
      mandates: advanceWithinBand(currentStats.mandates, heroKpiRanges.mandates, 1, 3),
      profiles: advanceWithinBand(currentStats.profiles, heroKpiRanges.profiles, 12, 32),
      briefings: advanceWithinBand(currentStats.briefings, heroKpiRanges.briefings, 1, 2),
      markets: advanceWithinBand(currentStats.markets, heroKpiRanges.markets, 1, 1),
    });

    const startTelemetry = () => {
      if (timerRef.current) return;

      timerRef.current = window.setInterval(() => {
        setStats((currentStats) => buildNextStats(currentStats));
      }, getRandomInRange(2200, 3200));
    };

    const stopTelemetry = () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setStats(heroKpiSeed);
    };

    const observer = new window.IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          startTelemetry();
        } else {
          stopTelemetry();
        }
      },
      {
        threshold: 0.35,
      }
    );

    if (stripRef.current) {
      observer.observe(stripRef.current);
    }

    return () => {
      stopTelemetry();
      observer.disconnect();
    };
  }, []);

  const items = [
    { value: stats.mandates, label: 'active searches', format: (n) => n.toString() },
    { value: stats.profiles, label: 'profiles in motion', format: (n) => n.toLocaleString() },
    { value: stats.briefings, label: 'signals published', format: (n) => n.toString() },
    { value: stats.markets, label: 'markets covered', format: (n) => n.toString() },
  ];

  return (
    <div
      ref={stripRef}
      className="mt-8 rounded-[24px] border border-white/10 bg-white/5 p-4 shadow-[0_20px_60px_rgba(0,0,0,0.18)] backdrop-blur-sm"
    >
      <div className="mb-4 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.26em] text-white/70">
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400/65 opacity-75" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400" />
        </span>
        LIVE FEED
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 sm:gap-6">
        {items.map((item) => (
          <div key={item.label} className="space-y-1.5">
            <div className="text-2xl font-semibold tracking-tight text-white sm:text-[1.7rem]">
              <AnimatedNumber value={item.value} format={item.format} />
            </div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-white/55">{item.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PrimaryButton({ children, onClick, className = '', type = 'button' }) {
  return (
    <button
      type={type}
      onClick={onClick}
      className={`inline-flex items-center justify-center rounded-xl bg-[#f7911d] px-5 py-3 text-sm font-semibold text-[#07111f] transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-[#f7911d]/20 ${className}`}
    >
      {children}
    </button>
  );
}

function SecondaryButton({ children, onClick, className = '', dark = false, type = 'button' }) {
  return (
    <button
      type={type}
      onClick={onClick}
      className={`inline-flex items-center justify-center rounded-xl border px-5 py-3 text-sm font-semibold transition-all hover:-translate-y-0.5 ${
        dark
          ? 'border-white/15 bg-white/5 text-white hover:bg-white/10'
          : 'border-[#d7dde7] bg-white text-[#07111f] hover:border-[#c8d0dc] hover:shadow-sm'
      } ${className}`}
    >
      {children}
    </button>
  );
}

function MiniCard({ title, description, dark = false, className = '' }) {
  return (
    <div
      className={`rounded-2xl border p-6 transition-all ${
        dark ? 'border-white/10 bg-white/5' : 'border-[#dce2eb] bg-white'
      } ${className}`}
    >
      <h3 className={`text-base font-semibold tracking-tight ${dark ? 'text-white' : 'text-[#07111f]'}`}>
        {title}
      </h3>
      <p className={`mt-2 text-sm leading-relaxed ${dark ? 'text-white/65' : 'text-[#64748b]'}`}>
      {description}
    </p>
  </div>
  );
}

function CapabilityCard({ capability }) {
  const Icon = capability.icon;

  return (
    <article className="group rounded-[22px] border border-[#dce2eb] bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-[#f3c38a] hover:shadow-md">
      <div
        aria-hidden="true"
        className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#f7911d]/10 text-[#f7911d]"
      >
        <Icon size={20} strokeWidth={1.8} />
      </div>

      <h3 className="mt-5 text-xl font-semibold tracking-tight text-[#07111f]">
        {capability.title}
      </h3>

      <p className="mt-3 max-w-md text-sm leading-relaxed text-[#64748b]">
        {capability.description}
      </p>
    </article>
  );
}

function ProfileCard({ profile, onRequestBrief }) {
  return (
    <article
      className="flex h-[520px] basis-[88%] snap-start flex-none flex-col overflow-hidden rounded-[24px] border border-white/10 bg-[#0c1425] shadow-[0_24px_60px_rgba(0,0,0,0.28)] sm:basis-[48%] lg:basis-[31%] xl:basis-[24%]"
      id={profile.id}
      tabIndex="0"
    >
      <div className="relative h-[190px] shrink-0 overflow-hidden">
        <img
          src={profile.image}
          alt={profile.imageAlt}
          className="h-full w-full object-cover"
          loading="lazy"
          style={{
            filter: 'grayscale(12%) saturate(85%) contrast(108%)',
            objectPosition: profile.imagePosition || '50% 50%',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#07111f] via-[#07111f]/20 to-transparent" />
        <div className="absolute left-4 top-4 rounded-full border border-[#f7911d]/25 bg-[#07111f]/80 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#f7911d]">
          {profile.id}
        </div>
        <div className="absolute bottom-4 left-4 right-4">
          <div className="text-[11px] uppercase tracking-[0.24em] text-white/60">{profile.market}</div>
          <h3 className="mt-1 text-xl font-semibold tracking-tight text-white">{profile.role}</h3>
          <div className="text-sm text-white/75">{profile.specialism}</div>
        </div>
      </div>

      <dl className="grid flex-1 grid-cols-2 grid-rows-[88px_minmax(0,1fr)] gap-3 p-5 pb-3">
        <div className="col-span-2 min-h-0 rounded-xl border border-white/10 bg-white/5 p-3">
          <dt className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/50">
            Market signal
          </dt>
          <dd className="mt-2 text-sm font-medium leading-5 text-white">{profile.marketSignal}</dd>
        </div>
        <div className="min-h-0 rounded-xl border border-white/10 bg-white/5 p-3">
          <dt className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/50">
            Market status
          </dt>
          <dd className="mt-2 text-sm leading-5 text-white/85">{profile.marketStatus}</dd>
        </div>
        <div className="min-h-0 rounded-xl border border-white/10 bg-white/5 p-3">
          <dt className="text-[10px] font-semibold uppercase tracking-[0.16em] text-white/50">
            Market relevance
          </dt>
          <dd className="mt-2 text-sm leading-5 text-[#46e6c8]">{profile.marketRelevance}</dd>
        </div>
      </dl>

      <div className="mt-auto px-5 pb-5">
        <button
          type="button"
          className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-[#f7911d] transition-colors hover:text-[#ffad4c] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
          onClick={(event) => onRequestBrief(profile, event.currentTarget)}
          aria-label={`Request a confidential brief for ${profile.id}, ${profile.role}`}
        >
          Request confidential brief
          <span aria-hidden="true">→</span>
        </button>
      </div>
    </article>
  );
}

function PublishingSupportCard({ item }) {
  return (
    <article className="rounded-[22px] border border-[#dce2eb] bg-white p-5 shadow-sm transition-shadow hover:shadow-md">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#f7911d]">{item.type}</div>
          <h3 className="mt-2 text-lg font-semibold tracking-tight text-[#07111f]">{item.title}</h3>
        </div>
        <div className="shrink-0 text-[11px] uppercase tracking-[0.22em] text-[#64748b]">{item.date}</div>
      </div>

      <p className="mt-3 text-sm leading-relaxed text-[#5b6475]">{item.description}</p>

      <div className="mt-4 flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-[0.18em] text-[#64748b]">{item.source}</span>
        <a
          href="/market-intelligence"
          className="text-sm font-semibold text-[#07111f] transition-colors hover:text-[#f7911d]"
        >
          Read analysis →
        </a>
      </div>
    </article>
  );
}

function ProcessCard({ step }) {
  return (
    <div className="rounded-2xl border border-[#dce2eb] bg-white p-6 shadow-sm">
      <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#f7911d]">{step.step}</div>
      <h3 className="mt-3 text-xl font-semibold tracking-tight text-[#07111f]">{step.title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-[#64748b]">{step.description}</p>
    </div>
  );
}

function OutcomeCard({ stat }) {
  return (
    <div className="rounded-2xl border border-[#dce2eb] bg-white p-6 text-left shadow-sm">
      <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#f7911d]">
        {stat.value}
      </div>
      <div className="mt-3 text-sm leading-6 text-[#64748b]">{stat.label}</div>
    </div>
  );
}

function NotificationToast({ notification, onClose }) {
  if (!notification.show) return null;

  const success = notification.type === 'success';

  return (
    <div
      className={`fixed right-4 top-4 z-[9999] flex w-[calc(100vw-2rem)] max-w-md items-start gap-3 rounded-2xl border px-4 py-4 shadow-2xl backdrop-blur-xl ${
        success
          ? 'border-emerald-500/20 bg-emerald-500 text-white'
          : 'border-rose-500/20 bg-rose-500 text-white'
      }`}
      role="alert"
      aria-live="polite"
    >
      <div className="mt-0.5 shrink-0">
        {success ? (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        ) : (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        )}
      </div>

      <div className="flex-1">
        <div className="text-sm font-semibold">{notification.title}</div>
        <div className="mt-1 text-sm/6 opacity-90">{notification.message}</div>
      </div>

      <button
        type="button"
        onClick={onClose}
        className="rounded-lg p-1 transition-colors hover:bg-white/10"
        aria-label="Close notification"
      >
        <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
}

function LeadModal({ open, mode, form, setForm, onClose, onSubmit, isSubmitting }) {
  const modalRef = useRef(null);
  const firstInputRef = useRef(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!open) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') onCloseRef.current();
    };

    const handleTab = (e) => {
      if (e.key !== 'Tab') return;
      const modal = modalRef.current;
      if (!modal) return;

      const focusableElements = modal.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      );

      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        }
      } else if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    };

    const focusTimer = window.setTimeout(() => firstInputRef.current?.focus(), 100);

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('keydown', handleTab);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('keydown', handleTab);
    };
  }, [open]);

  if (!open) return null;

  const copy = modalCopy[mode] || modalCopy.general;

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-[#07111f]/70 px-4 py-6 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lead-modal-title"
      aria-describedby="lead-modal-description"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={modalRef}
        className="relative w-full max-w-xl overflow-hidden rounded-[28px] border border-white/10 bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-slate-200 bg-[#f8fafc] px-6 py-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#f7911d]">
                Candid-I / Private enquiry
              </div>
              <h3 id="lead-modal-title" className="mt-2 text-2xl font-semibold tracking-tight text-[#07111f]">
                {copy.title}
              </h3>
              <p id="lead-modal-description" className="mt-2 text-sm leading-relaxed text-[#64748b]">
                {copy.subtitle}
              </p>
            </div>

            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-200 bg-white p-2 text-slate-500 transition-colors hover:text-[#07111f]"
              aria-label="Close modal"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <form onSubmit={onSubmit} className="max-h-[80vh] overflow-y-auto px-6 py-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-[#07111f]">
                First name <span className="text-[#f7911d]">*</span>
              </label>
              <input
                ref={firstInputRef}
                type="text"
                required
                autoComplete="given-name"
                value={form.firstName}
                onChange={(e) => setForm((prev) => ({ ...prev, firstName: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-[#f7911d] focus:ring-4 focus:ring-[#f7911d]/10"
                placeholder="Kevin"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-[#07111f]">
                Last name <span className="text-[#f7911d]">*</span>
              </label>
              <input
                type="text"
                required
                autoComplete="family-name"
                value={form.lastName}
                onChange={(e) => setForm((prev) => ({ ...prev, lastName: e.target.value }))}
                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-[#f7911d] focus:ring-4 focus:ring-[#f7911d]/10"
                placeholder="Matthews"
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="mb-2 block text-sm font-medium text-[#07111f]">
              Business email <span className="text-[#f7911d]">*</span>
            </label>
            <input
              type="email"
              required
              autoComplete="email"
              value={form.businessEmail}
              onChange={(e) => setForm((prev) => ({ ...prev, businessEmail: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-[#f7911d] focus:ring-4 focus:ring-[#f7911d]/10"
              placeholder="you@company.com"
            />
          </div>

          <div className="mt-4">
            <label className="mb-2 block text-sm font-medium text-[#07111f]">
              Organisation / current company <span className="text-[#f7911d]">*</span>
            </label>
            <input
              type="text"
              required
              value={form.organisation}
              onChange={(e) => setForm((prev) => ({ ...prev, organisation: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-[#f7911d] focus:ring-4 focus:ring-[#f7911d]/10"
              placeholder="Company name"
            />
          </div>

          {mode === 'profile' ? (
            <div className="mt-4">
              <label className="mb-2 block text-sm font-medium text-[#07111f]">Profile</label>
              <input
                type="text"
                readOnly
                value={form.profileReference}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600"
              />
            </div>
          ) : null}

          <div className="mt-4">
            <label className="mb-2 block text-sm font-medium text-[#07111f]">
              What are you interested in <span className="text-[#f7911d]">*</span>
            </label>
            <select
              required
              value={form.interest}
              onChange={(e) => setForm((prev) => ({ ...prev, interest: e.target.value }))}
              className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition-all focus:border-[#f7911d] focus:ring-4 focus:ring-[#f7911d]/10"
            >
              <option value="">Select one</option>
              <option value="Hiring support">Hiring support</option>
              <option value="Join the talent network">Join the talent network</option>
              <option value="Market intelligence">Market intelligence</option>
              <option value="Confidential profile brief">Confidential profile brief</option>
              <option value="Reports and publishing">Reports and publishing</option>
              <option value="General enquiry">General enquiry</option>
            </select>
          </div>

          {mode === 'profile' ? (
            <p className="mt-4 text-xs leading-5 text-slate-500">
              Candidate information is shared only where appropriate and with due regard for confidentiality.
            </p>
          ) : null}

          <div className="mt-4">
            <label className="mb-2 block text-sm font-medium text-[#07111f]">
              Brief context <span className="text-[#f7911d]">*</span>
            </label>
            <textarea
              required
              rows={4}
              value={form.brief}
              onChange={(e) => setForm((prev) => ({ ...prev, brief: e.target.value }))}
              className="w-full resize-none rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-[#f7911d] focus:ring-4 focus:ring-[#f7911d]/10"
              placeholder="Tell us what you are hiring for, what market you want to track, or what kind of opportunities you want to see."
            />
          </div>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <PrimaryButton type="submit" className="w-full sm:w-auto" onClick={() => {}}>
              {isSubmitting ? 'Submitting...' : copy.button}
            </PrimaryButton>
            <SecondaryButton type="button" className="w-full sm:w-auto" onClick={onClose}>
              Cancel
            </SecondaryButton>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Landing() {
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [leadMode, setLeadMode] = useState('general');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedSector, setSelectedSector] = useState('All');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState({
    show: false,
    title: '',
    message: '',
    type: 'success',
  });
  const [leadForm, setLeadForm] = useState(defaultLeadForm);
  const [showFeaturedVideo, setShowFeaturedVideo] = useState(false);
  const carouselRef = useRef(null);
  const leadModalTriggerRef = useRef(null);
  const leadContextRef = useRef({ ctaLocation: 'unknown' });
  const videoMilestonesRef = useRef(new Set());
  const [canScrollPrevious, setCanScrollPrevious] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(true);

  useEffect(() => {
    document.title = 'Candid-I — Talent Intelligence';

    const fontLink = document.createElement('link');
    fontLink.rel = 'stylesheet';
    fontLink.href =
      'https://fonts.googleapis.com/css2family=Inter:wght@400;500;600;700;800&family=IBM+Plex+Mono:wght@400;500;600&display=swap';
    document.head.appendChild(fontLink);

    document.documentElement.style.scrollBehavior = 'smooth';

    return () => {
      document.head.removeChild(fontLink);
      document.documentElement.style.scrollBehavior = 'auto';
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = showLeadModal ? 'hidden' : 'auto';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [showLeadModal]);

  useEffect(() => {
    if (!notification.show) return;
    const timer = setTimeout(() => {
      setNotification({ show: false, title: '', message: '', type: 'success' });
    }, 5000);

    return () => clearTimeout(timer);
  }, [notification.show]);

  const filteredProfiles = useMemo(() => {
    if (selectedSector === 'All') return talentProfiles;
    return talentProfiles.filter((profile) => profile.sector === selectedSector);
  }, [selectedSector]);

  const updateCarouselControls = useCallback(() => {
    const el = carouselRef.current;
    if (!el) return;
    const maxScrollLeft = Math.max(0, el.scrollWidth - el.clientWidth);
    setCanScrollPrevious(el.scrollLeft > 2);
    setCanScrollNext(el.scrollLeft < maxScrollLeft - 2);
  }, []);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return undefined;

    el.scrollTo({ left: 0, behavior: 'auto' });
    const frame = window.requestAnimationFrame(updateCarouselControls);
    const handleResize = () => updateCarouselControls();
    window.addEventListener('resize', handleResize);

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', handleResize);
    };
  }, [selectedSector, filteredProfiles.length, updateCarouselControls]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setMobileMenuOpen(false);
  };

  const openLeadModal = (mode = 'general', profile = null, trigger = null, ctaLocation = 'unknown') => {
    leadModalTriggerRef.current = trigger;
    leadContextRef.current = { ctaLocation };
    trackLandingEvent('landing_cta_click', { mode, ctaLocation });
    setLeadMode(mode);
    setLeadForm({
      ...defaultLeadForm,
      interest: modalCopy[mode].interest || modalCopy.general.interest,
      profileReference: profile ? `${profile.id} — ${profile.role}` : '',
    });
    setShowLeadModal(true);
    setMobileMenuOpen(false);
  };

  const closeLeadModal = () => {
    setShowLeadModal(false);
    setLeadForm(defaultLeadForm);
    window.requestAnimationFrame(() => leadModalTriggerRef.current?.focus());
  };

  const showNotification = (title, message, type = 'success') => {
    setNotification({ show: true, title, message, type });
  };

  const handleBookCall = (ctaLocation = 'unknown', trigger = null) => {
    openLeadModal('general', null, trigger, ctaLocation);
  };

  const openFeaturedVideo = () => {
    setShowFeaturedVideo(true);
    trackLandingEvent('featured_video_play', { title: publishingData.featured.title });
  };

  const handleVideoProgress = (event) => {
    const video = event.currentTarget;
    if (!video.duration) return;
    const percent = Math.floor((video.currentTime / video.duration) * 100);

    [25, 50, 75].forEach((milestone) => {
      if (percent >= milestone && !videoMilestonesRef.current.has(milestone)) {
        videoMilestonesRef.current.add(milestone);
        trackLandingEvent('featured_video_progress', {
          title: publishingData.featured.title,
          percent: milestone,
        });
      }
    });
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    setIsSubmitting(true);

    const payload = {
      ...leadForm,
      inquiryType: leadMode,
      campaignSource: 'landing-page',
      ctaLocation: leadContextRef.current.ctaLocation,
    };

    try {
      await submitPublicEnquiry(payload);

      closeLeadModal();

      showNotification(
        'Request received',
        leadMode === 'candidate'
          ? 'Thanks - your profile has been received. We will be in touch with relevant opportunities.'
          : leadMode === 'intelligence'
          ? 'Thanks - your intelligence request has been received. We will review the brief.'
          : 'Thanks - your role brief has been received. We will review the market and respond within two business days.',
        'success'
      );
    } catch (error) {
      showNotification(
        'Submission error',
        error.message || 'There was an issue submitting your request.',
        'error'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCarousel = (direction) => {
    const el = carouselRef.current;
    if (!el) return;
    const distance = 360;
    el.scrollBy({
      left: direction === 'next' ? distance : -distance,
      behavior: 'smooth',
    });
  };

  return (
    <div
      className="min-h-screen bg-[#f8fafc] text-[#07111f]"
      style={{
        fontFamily:
          'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      }}
    >
      <NotificationToast
        notification={notification}
        onClose={() => setNotification({ show: false, title: '', message: '', type: 'success' })}
      />

      <header className="sticky top-0 z-[1000] border-b border-white/10 bg-[#07111f]/95 backdrop-blur-xl">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <a href="#hero" className="flex items-center gap-3" aria-label="Candid-I home">
            <img
              src={candidILogo}
              alt="Candid-I"
              className="h-10 w-auto max-w-[150px] object-contain"
            />
          </a>

          <nav className="hidden items-center gap-8 lg:flex">
            <a href="/employers" className="text-sm font-medium tracking-wide text-white/80 transition-colors hover:text-white">
              Employers
            </a>
            <a href="/talent" className="text-sm font-medium tracking-wide text-white/80 transition-colors hover:text-white">
              Candidates
            </a>
            <a href="/market-intelligence" className="text-sm font-medium tracking-wide text-white/80 transition-colors hover:text-white">
              Market Intelligence
            </a>
            <a href="/market-intelligence" className="text-sm font-medium tracking-wide text-white/80 transition-colors hover:text-white">
              Reports
            </a>
            <a href="#about" className="text-sm font-medium tracking-wide text-white/80 transition-colors hover:text-white">
              About
            </a>
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            <SecondaryButton
              dark
              onClick={(event) => handleBookCall('desktop-navigation', event.currentTarget)}
            >
              Book a Call
            </SecondaryButton>
          </div>

          <button
            type="button"
            className="inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/5 p-2 text-white lg:hidden"
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
            onClick={() => setMobileMenuOpen((prev) => !prev)}
          >
            {mobileMenuOpen ? (
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {mobileMenuOpen ? (
          <div className="border-t border-white/10 bg-[#07111f] px-4 py-5 lg:hidden">
            <div className="mx-auto max-w-7xl space-y-4">
              <div className="grid gap-3">
                <a
                  href="/employers"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white/85 transition-colors hover:bg-white/10"
                >
                  Employers
                </a>
                <a
                  href="/talent"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white/85 transition-colors hover:bg-white/10"
                >
                  Candidates
                </a>
                <a
                  href="/market-intelligence"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white/85 transition-colors hover:bg-white/10"
                >
                  Intelligence
                </a>
                <a
                  href="/market-intelligence"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white/85 transition-colors hover:bg-white/10"
                >
                  Reports
                </a>
                <a
                  href="#about"
                  onClick={() => setMobileMenuOpen(false)}
                  className="rounded-2xl border border-white/10 bg-white/5 px-4 py-3 text-white/85 transition-colors hover:bg-white/10"
                >
                  About
                </a>
              </div>

              <div className="flex flex-col gap-3 pt-2 sm:flex-row">
                <PrimaryButton
                  onClick={(event) => handleBookCall('mobile-navigation', event.currentTarget)}
                  className="w-full sm:w-auto"
                >
                  Book a Call
                </PrimaryButton>
                <SecondaryButton dark onClick={() => openLeadModal('employer')} className="w-full sm:w-auto">
                  Hire with us
                </SecondaryButton>
              </div>
            </div>
          </div>
        ) : null}
      </header>

      {/* Hero */}
      <section id="hero" className="relative overflow-hidden bg-[#07111f]">
        <div
          className="absolute inset-0 opacity-70"
          style={{
            background:
              'radial-gradient(circle at 20% 20%, rgba(247,145,29,0.18), transparent 30%), radial-gradient(circle at 80% 10%, rgba(255,255,255,0.08), transparent 28%), radial-gradient(circle at 60% 80%, rgba(247,145,29,0.08), transparent 30%)',
          }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-[linear-gradient(to_bottom,rgba(7,17,31,0.05),rgba(7,17,31,0.25))]" aria-hidden="true" />

        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
            <div className="max-w-2xl">
              <SectionLabel dark>ALICE — CANDID-I’S MARKET INTELLIGENCE ENGINE</SectionLabel>

              <h1 className="max-w-3xl text-5xl font-semibold tracking-tight sm:text-6xl lg:text-7xl">
                <span className="block text-white">ALICE reads the</span>
                <span className="block text-white">market.</span>
                <span className="block text-[#f7911d]">Humans decide</span>
                <span className="block text-[#f7911d]">what it means.</span>
              </h1>

              <p className="mt-6 max-w-[580px] text-base leading-7 text-slate-300 sm:text-lg sm:leading-8">
                ALICE is Candid-I&apos;s market intelligence engine for hiring. It maps talent markets, identifies relevant
                professionals and surfaces the signals behind better decisions.
              </p>

              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <PrimaryButton onClick={() => window.location.assign('/employers')} className="w-full sm:w-auto">
                  Hire with us
                </PrimaryButton>
                <SecondaryButton dark onClick={() => window.location.assign('/talent')} className="w-full sm:w-auto">
                  Join the talent network
                </SecondaryButton>
                <SecondaryButton dark onClick={() => window.location.assign('/market-intelligence/briefing')} className="w-full sm:w-auto">
                  Join the market briefing
                </SecondaryButton>
              </div>

              <div className="mt-10 h-px w-full max-w-2xl bg-white/10" />

              <HeroKpiStrip />
            </div>

            <div className="relative">
              <div className="relative overflow-hidden rounded-[32px] border border-white/10 bg-white/5 p-4 shadow-[0_30px_90px_rgba(0,0,0,0.42)]">
                <div className="relative overflow-hidden rounded-[26px] border border-white/10 bg-[#07111f]">
                  <img
                    src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&w=1600&q=80"
                    alt="Featured talent profile"
                    className="h-[520px] w-full object-cover"
                    loading="eager"
                    style={{ filter: 'grayscale(12%) saturate(82%) contrast(108%)' }}
                  />
                  <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(7,17,31,0.85),rgba(7,17,31,0.12))]" />
                  <div className="absolute left-4 top-4 flex items-center gap-2">
                    <span className="rounded-full border border-white/10 bg-[#07111f]/80 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-white/80">
                      Featured profile
                    </span>
                    <span className="rounded-full border border-[#f7911d]/25 bg-[#07111f]/80 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#f7911d]">
                      Live
                    </span>
                  </div>

                  <div className="absolute left-4 right-4 bottom-4 rounded-[22px] border border-white/10 bg-[#07111f]/88 p-4 backdrop-blur-sm">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#f7911d]">
                          ALICE live signal map
                        </div>
                        <div className="mt-2 text-2xl font-semibold tracking-tight text-white">
                          APAC Commercial VP
                        </div>
                        <div className="mt-1 text-sm text-white/65">Singapore / Hong Kong</div>
                      </div>
                      <div className="rounded-lg border border-[#f7911d]/25 bg-[#f7911d]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-[#f7911d]">
                        Streaming
                      </div>
                    </div>

                    <div className="mt-4 grid gap-3 sm:grid-cols-3">
                      <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-white/40">Target role</div>
                        <div className="mt-1 text-sm font-medium text-white">APAC Commercial VP</div>
                      </div>
                      <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-white/40">Signal origin</div>
                        <div className="mt-1 text-sm font-medium text-white">Singapore / Hong Kong</div>
                      </div>
                      <div className="rounded-xl border border-white/10 bg-white/5 p-3">
                        <div className="text-[11px] uppercase tracking-[0.18em] text-white/40">Match index</div>
                        <div className="mt-1 text-sm font-semibold text-[#38d9a9]">94.2% verified fit</div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  className="pointer-events-none absolute -right-10 -top-10 h-36 w-36 rounded-full bg-[#f7911d]/10 blur-3xl"
                  aria-hidden="true"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Audience split */}
      <section className="scroll-mt-28 bg-[#f8fafc] px-4 py-20 sm:px-6 lg:px-8" id="audience-paths">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            eyebrow="BUILT FOR BOTH SIDES OF THE MARKET"
            title={
              <>
                One intelligence layer.
                <br />
                Two ways to move with confidence.
              </>
            }
            subtitle="Candid-I gives employers and candidates early access to the market signal — and the context to know what to do next."
          />

          <p className="mt-6 max-w-3xl text-base leading-7 text-slate-600">
            Candid-I combines executive search, talent mapping and market intelligence in one human-led service powered by
            ALICE.
          </p>

          <div className="mt-14 grid gap-6 lg:grid-cols-2">
            {audiencePaths.map((path) => (
              <div
                key={path.id}
                id={path.id}
                className="scroll-mt-28 rounded-[28px] border border-[#dce2eb] bg-white p-8 shadow-sm transition-shadow hover:shadow-md"
              >
                <SectionLabel>{path.eyebrow}</SectionLabel>
                <h3 className="text-3xl font-semibold tracking-tight text-[#07111f]">{path.title}</h3>
                <p className="mt-4 max-w-2xl text-base leading-7 text-[#64748b]">{path.description}</p>
                {path.id === 'employers' ? (
                  <p className="mt-4 max-w-2xl text-sm leading-6 text-slate-500">
                    Tell us what you need. We define the market, map relevant talent and return with an evidence-led search
                    strategy.
                  </p>
                ) : null}

                <ul className="mt-6 space-y-3">
                  {path.bullets.map((bullet) => (
                    <li key={bullet} className="flex items-start gap-3 text-sm text-[#415064]">
                      <span className="mt-1 inline-block h-2.5 w-2.5 rounded-full bg-[#f7911d]" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                  <PrimaryButton
                    onClick={() =>
                      path.id === 'employers'
                        ? openLeadModal('employer')
                        : window.location.assign('/talent')
                    }
                  >
                    {path.cta}
                  </PrimaryButton>
                  <SecondaryButton onClick={() => window.location.assign('/market-intelligence/briefing')}>
                    Join the market briefing
                  </SecondaryButton>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 rounded-[24px] border border-[#dce2eb] bg-white p-5 shadow-sm">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              {clientOutputs.map(({ label, icon: Icon }) => (
                <div key={label} className="flex min-h-16 items-center gap-3 rounded-2xl bg-slate-50 px-4 py-3">
                  <Icon className="h-5 w-5 shrink-0 text-[#f7911d]" aria-hidden="true" />
                  <span className="text-sm font-medium leading-5 text-[#07111f]">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="bg-[#f8fafc] px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-3xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#f7911d]">
              MARKET CONTEXT
            </p>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-[#07111f] sm:text-4xl lg:text-[3.1rem] lg:leading-tight">
              Hiring decisions slow when the market moves faster than the data.
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
              Search teams lose time when visibility is partial, timing is unclear, and signals are fragmented.
            </p>
          </div>

          <div className="mt-12 grid gap-4 md:grid-cols-2">
            {problemCards.map((card) => (
              <article
                key={card.title}
                className="flex h-full flex-col rounded-[24px] border border-slate-200 bg-white p-6 shadow-[0_10px_30px_rgba(15,23,42,0.05)]"
              >
                <h3 className="text-[1.05rem] font-semibold tracking-tight text-[#07111f] sm:text-[1.15rem]">
                  {card.title}
                </h3>
                <p className="mt-3 max-w-prose text-sm leading-relaxed text-slate-600 sm:text-[0.98rem]">
                  {card.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Solution overview */}
      <section className="bg-[#07111f] px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            dark
            align="center"
            eyebrow="Operating model"
            title="Monitor → Interpret → Act"
            subtitle="ALICE monitors the market in real time. Human judgment turns signal into decisions, outreach, and publishing."
          />

          <div className="mt-12 grid gap-4 lg:grid-cols-3">
            {solutionSteps.map((step) => (
              <div
                key={step.step}
                className="rounded-[24px] border border-white/10 bg-white/5 p-7 backdrop-blur-sm"
              >
                <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#f7911d]">
                  Phase {step.step}
                </div>
                <h3 className="mt-3 text-2xl font-semibold tracking-tight text-white">{step.title}</h3>
                <p className="mt-3 text-sm font-medium leading-relaxed text-[#d8e0ec]">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ALICE capabilities */}
      <section className="bg-[#f8fafc] px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="max-w-4xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[#f7911d]">
              HOW ALICE WORKS
            </p>

            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-[#07111f] sm:text-4xl lg:text-[3.1rem] lg:leading-tight">
              Powered by ALICE. Led by humans.
            </h2>

            <p className="mt-4 max-w-4xl text-base leading-relaxed text-slate-600 sm:text-lg">
              ALICE gathers and structures market signals. Our talent teams apply human judgement, relationships and context to turn them into hiring decisions.
            </p>
          </div>

          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {aliceCapabilities.map((capability) => (
              <CapabilityCard key={capability.id} capability={capability} />
            ))}
          </div>
        </div>
      </section>

      {/* Intelligence carousel */}
      <section
        id="intelligence"
        aria-labelledby="talent-radar-title"
        className="scroll-mt-28 bg-[#07111f] px-4 py-20 sm:px-6 lg:px-8"
      >
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <SectionHeading
              dark
              titleId="talent-radar-title"
              eyebrow="Talent signals"
              title="Talent Intelligence Radar"
              subtitle="Explore selected talent signals across Candid-I’s specialist markets. Each profile shows relevant experience, market position and potential value to a hiring mandate."
            />

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => handleCarousel('prev')}
                disabled={!canScrollPrevious}
                className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-35"
                aria-label="Show previous talent profiles"
              >
                ←
              </button>
              <button
                type="button"
                onClick={() => handleCarousel('next')}
                disabled={!canScrollNext}
                className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-white transition-colors hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-35"
                aria-label="Show next talent profiles"
              >
                →
              </button>
            </div>
          </div>

          <div className="mt-8 flex gap-2 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            {talentFilters.map((filter) => (
              <button
                key={filter}
                type="button"
                onClick={() => setSelectedSector(filter)}
                aria-pressed={selectedSector === filter}
                className={`rounded-full border px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] transition-all ${
                  selectedSector === filter
                    ? 'border-[#f7911d] bg-[#f7911d] text-[#07111f]'
                    : 'border-white/10 bg-white/5 text-white/70 hover:bg-white/10'
                }`}
              >
                {filter}
              </button>
            ))}
          </div>

          <div
            ref={carouselRef}
            onScroll={updateCarouselControls}
            className="mt-8 flex snap-x snap-mandatory gap-5 overflow-x-auto pb-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            {filteredProfiles.map((profile) => (
              <ProfileCard
                key={profile.id}
                profile={profile}
                onRequestBrief={(selectedProfile, trigger) =>
                  openLeadModal('profile', selectedProfile, trigger)
                }
              />
            ))}
          </div>
          <p className="mt-6 text-xs leading-5 text-white/45">
            Illustrative talent signals. Profile details have been anonymised.
          </p>
        </div>
      </section>

      {/* Publishing */}
      <section id="publishing" className="scroll-mt-28 bg-[#f8fafc] px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <SectionHeading
              eyebrow="RESEARCH & INTELLIGENCE"
              title="Market intelligence, made visible."
              subtitle="Candid-I turns talent movement, hiring demand and competitive activity into market maps, executive briefings and decision-ready analysis."
            />

            <div className="text-sm uppercase tracking-[0.2em] text-[#64748b]">
              BRIEFINGS · TALENT MAPS · MARKET ANALYSIS
            </div>
          </div>

          <p className="mt-5 max-w-3xl text-base leading-7 text-slate-600">
            This intelligence informs the market maps, search strategies and hiring recommendations delivered to clients.
          </p>

          <div className="mt-12 grid gap-6 lg:grid-cols-[1.18fr_0.82fr]">
            {/* Featured story */}
            <article className="overflow-hidden rounded-[28px] border border-[#dce2eb] bg-[#07111f] shadow-[0_24px_70px_rgba(7,17,31,0.12)]">
              <div className="relative">
                {showFeaturedVideo ? (
                  <video
                    src={publishingData.featured.videoUrl}
                    poster={publishingData.featured.image}
                    className="h-[340px] w-full bg-black object-cover"
                    controls
                    autoPlay
                    muted
                    playsInline
                    preload="metadata"
                    onTimeUpdate={handleVideoProgress}
                    onEnded={() =>
                      trackLandingEvent('featured_video_complete', { title: publishingData.featured.title })
                    }
                  >
                    Your browser does not support HTML video.
                  </video>
                ) : (
                  <>
                    <img
                      src={publishingData.featured.image}
                      alt={publishingData.featured.title}
                      className="h-[340px] w-full object-cover"
                      loading="lazy"
                      style={{ filter: 'grayscale(10%) saturate(85%) contrast(108%)' }}
                    />
                    <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(7,17,31,0.88),rgba(7,17,31,0.05))]" />
                  </>
                )}
                <div className="absolute left-5 top-5 rounded-full border border-white/10 bg-[#07111f]/70 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-[#f7911d]">
                  {publishingData.featured.type}
                </div>
                <div className="absolute right-5 top-5 rounded-full border border-white/10 bg-[#07111f]/70 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-white/70">
                  {publishingData.featured.time}
                </div>
                <button
                  type="button"
                  className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-white/10 bg-[#f7911d] px-5 py-4 text-sm font-semibold text-[#07111f] shadow-2xl shadow-black/30 transition-transform hover:scale-105 ${
                    showFeaturedVideo ? 'hidden' : 'inline-flex'
                  }`}
                  aria-label="Play featured briefing"
                  onClick={openFeaturedVideo}
                >
                  ▶
                </button>
              </div>

              <div className="p-6">
                <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[#f7911d]">
                  Featured market brief
                </div>
                <h3 className="mt-3 text-3xl font-semibold tracking-tight text-white">
                  {publishingData.featured.title}
                </h3>
                <p className="mt-3 max-w-2xl text-base leading-7 text-white/70">
                  {publishingData.featured.description}
                </p>
                <div className="mt-6 flex items-center justify-between gap-4 border-t border-white/10 pt-5">
                  <div className="text-[11px] uppercase tracking-[0.22em] text-white/45">
                    Published {publishingData.featured.date}
                  </div>
                  <button
                    type="button"
                    className="text-sm font-semibold text-[#f7911d] transition-colors hover:text-[#ffb85c]"
                    onClick={openFeaturedVideo}
                  >
                    Watch briefing →
                  </button>
                </div>
              </div>
            </article>

            {/* Supporting articles */}
            <div className="space-y-4">
              {publishingData.items.map((item) => (
                <PublishingSupportCard key={item.title} item={item} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Value */}
      <section className="bg-[#f8fafc] px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            eyebrow="Why it matters"
            title="One market. Value on both sides."
            subtitle="Employers gain visibility. Professionals gain relevant access. Better information creates better outcomes for both."
          />

          <div className="mt-12 grid gap-6 lg:grid-cols-2">
            <div className="rounded-[28px] border border-[#dce2eb] bg-white p-8 shadow-sm">
              <SectionLabel>Employer value proposition</SectionLabel>
              <h3 className="text-3xl font-semibold tracking-tight text-[#07111f]">Why employers use Candid-I</h3>
              <ul className="mt-6 space-y-3">
                {employerValue.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm leading-7 text-[#415064]">
                    <span className="mt-1 h-2.5 w-2.5 rounded-full bg-[#f7911d]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-[28px] border border-[#dce2eb] bg-white p-8 shadow-sm">
              <SectionLabel>Professional value proposition</SectionLabel>
              <h3 className="text-3xl font-semibold tracking-tight text-[#07111f]">Why professionals engage</h3>
              <ul className="mt-6 space-y-3">
                {candidateValue.map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm leading-7 text-[#415064]">
                    <span className="mt-1 h-2.5 w-2.5 rounded-full bg-[#f7911d]" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Human-led intelligence */}
      <section id="about" className="scroll-mt-28 bg-[#07111f] px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
          <div>
            <SectionHeading
              dark
              eyebrow="HUMAN-LED INTELLIGENCE"
              title="Technology finds the signal. Experience determines what matters."
              subtitle="ALICE structures the evidence. Candid-I's specialists validate relevance and recommend the next action."
            />

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <PrimaryButton onClick={() => window.location.assign('/market-intelligence/briefing')}>
                Join the market briefing
              </PrimaryButton>
            </div>
          </div>

          <div className="rounded-[28px] border border-white/10 bg-white/5 p-6 backdrop-blur-sm">
            <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#f7911d]">
              Market synthesis
            </div>

            <div className="mt-5 space-y-3">
              <div className="rounded-2xl border border-white/10 bg-[#07111f] px-4 py-3 text-sm text-white/80">
                1. Market signals gathered
              </div>
              <div className="rounded-2xl border border-white/10 bg-[#07111f] px-4 py-3 text-sm text-white/80">
                2. Patterns structured by ALICE
              </div>
              <div className="rounded-2xl border border-white/10 bg-[#07111f] px-4 py-3 text-sm text-white/80">
                3. Candid-I specialist interprets the evidence
              </div>
            </div>

            <div className="mt-5 rounded-2xl border border-[#f7911d]/20 bg-[#f7911d]/10 px-4 py-3 text-sm text-[#f7911d]">
              Human judgement determines what is relevant, what should be validated and what action to recommend.
            </div>
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="bg-[#f8fafc] px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            eyebrow="Commercial outcomes"
            title="What changes for the client."
            subtitle="The value is measured in faster market understanding, stronger candidate engagement and more confident hiring decisions."
          />

          <div className="mt-12 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            {outcomes.map((stat) => (
              <OutcomeCard key={stat.label} stat={stat} />
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-[#f8fafc] px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            eyebrow="How it works"
            title="A clear path from question to decision."
            subtitle="Every assignment follows the same discipline: define the question, build the market view and turn the evidence into action."
          />

          <div className="mt-12 grid gap-4 lg:grid-cols-3">
            {processSteps.map((step) => (
              <ProcessCard key={step.step} step={step} />
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-[#07111f] px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-5xl text-center">
          <SectionLabel dark className="justify-center">
            Ready to activate Candid-I
          </SectionLabel>
          <h2 className="text-4xl font-semibold tracking-tight text-white sm:text-5xl">
            Build a market intelligence system for hiring decisions.
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-7 text-white/70">
            Employers can submit a role. Candidates can join the network. Decision-makers can explore the reports.
          </p>

          <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <PrimaryButton onClick={(event) => handleBookCall('final-cta', event.currentTarget)}>
              Book a Call
            </PrimaryButton>
            <SecondaryButton dark onClick={(event) => openLeadModal('employer', null, event.currentTarget, 'final-cta')}>
              Hire with us
            </SecondaryButton>
            <button
              type="button"
              onClick={() => window.location.assign('/talent')}
              className="inline-flex min-h-11 items-center justify-center px-4 text-sm font-semibold text-white/70 transition-colors hover:text-white"
            >
              Join the talent network
            </button>
            <button
              type="button"
              onClick={() => window.location.assign('/market-intelligence/briefing')}
              className="inline-flex min-h-11 items-center justify-center px-4 text-sm font-semibold text-white/70 transition-colors hover:text-white"
            >
              Join the market briefing
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-[#07111f] px-4 py-10 sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-7xl flex-col gap-8 md:flex-row md:items-start md:justify-between">
          <div className="max-w-md">
            <div className="flex items-center gap-3">
              <img
                src={candidILogo}
                alt="Candid-I"
                className="h-10 w-auto max-w-[150px] object-contain"
              />
            </div>
            <p className="mt-4 text-sm leading-7 text-white/65">
              Candid-I is a talent intelligence platform for employers, candidates, and market readers.
              ALICE monitors the market. Humans decide what it means.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-8 sm:grid-cols-3">
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#f7911d]">Explore</div>
              <div className="mt-4 space-y-3 text-sm text-white/65">
                <a href="/employers" className="block transition-colors hover:text-white">
                  Employers
                </a>
                <a href="/talent" className="block transition-colors hover:text-white">
                  Candidates
                </a>
                <a href="/market-intelligence" className="block transition-colors hover:text-white">
                  Reports
                </a>
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#f7911d]">Intelligence</div>
              <div className="mt-4 space-y-3 text-sm text-white/65">
                <a href="#intelligence" className="block transition-colors hover:text-white">
                  Talent radar
                </a>
                <a href="#about" className="block transition-colors hover:text-white">
                  Human-led publishing
                </a>
                <button
                  type="button"
                  onClick={() => window.location.assign('/market-intelligence/briefing')}
                  className="block text-left transition-colors hover:text-white"
                >
                  Join the market briefing
                </button>
              </div>
            </div>

            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.22em] text-[#f7911d]">Contact</div>
              <div className="mt-4 space-y-3 text-sm text-white/65">
                <button
                  type="button"
                  onClick={(event) => handleBookCall('footer', event.currentTarget)}
                  className="block transition-colors hover:text-white"
                >
                  Book a call
                </button>
                <a href="mailto:hello@candid-i.com" className="block transition-colors hover:text-white">
                  hello@candid-i.com
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="mx-auto mt-10 flex max-w-7xl flex-col gap-2 border-t border-white/10 pt-6 text-xs uppercase tracking-[0.2em] text-white/40 sm:flex-row sm:items-center sm:justify-between">
          <div>© {new Date().getFullYear()} Candid-I. All rights reserved.</div>
          <div className="flex gap-4">
            <span>Privacy</span>
            <span>Terms</span>
            <span>Security</span>
          </div>
        </div>
      </footer>

      <LeadModal
        open={showLeadModal}
        mode={leadMode}
        form={leadForm}
        setForm={setLeadForm}
        onClose={closeLeadModal}
        onSubmit={handleLeadSubmit}
        isSubmitting={isSubmitting}
      />
    </div>
  );
}
